import { SCENES } from '../config/constants.js';

/**
 * O facilitador conduz a estação grupo a grupo (plano `estacao-facilitador`):
 * o tablet obedece ao estado do próprio grupo. Aqui fica a regra pura do que
 * fazer com cada mudança; quem mexe em cena e em tela é o `main.js`.
 */

/** Cenas e progresso salvo de cada fase. `resultado` são as cenas de pontuação, onde o jogador permanece depois de FINALIZAR. */
export const FASES_DO_GRUPO = Object.freeze({
    // AP10 (RN-AP-042): o Scan Pixel fecha com o líder; quem ainda joga sai quando ele conclui.
    scanpixel: {
        cenas: [SCENES.GAME, SCENES.SCAN_RESULTADO], resultado: [SCENES.SCAN_RESULTADO],
        chave: 'fiat-arcade:scanpixel:v1:', sairAoFinalizarSozinho: true
    },
    labpixels: { cenas: [SCENES.QUIZ, SCENES.LAB_RESULTADO], resultado: [SCENES.LAB_RESULTADO], chave: 'fiat-arcade:labpixels:v1:' },
    player1: { cenas: [SCENES.PLAYER1, SCENES.PLAYER1_RESULTADO], resultado: [SCENES.PLAYER1_RESULTADO], chave: 'fiat-arcade:player1:v1:' },
    versus: {
        cenas: [SCENES.VERSUS, SCENES.VERSUS_ARGUMENTO, SCENES.VERSUS_RESULTADO],
        resultado: [SCENES.VERSUS_RESULTADO],
        chave: 'fiat-arcade:versus:v1:'
    }
});

/** Faixa mostrada a quem ficou parado na fase que o facilitador finalizou. */
const TEXTO_ENCERRADA = 'ATIVIDADE ENCERRADA PELO FACILITADOR';

export const MUDANCA = Object.freeze({
    INICIADA: 'iniciada', PAUSADA: 'pausada', RETOMADA: 'retomada',
    FINALIZADA: 'finalizada', REINICIADA: 'reiniciada'
});

/**
 * Compara o estado que o tablet conhecia com o que chegou e diz o que mudou.
 * `null` quando nada muda (evento repetido ou atrasado).
 *
 * @param {{estado: string, ciclo: number, liberado?: boolean}|null} anterior
 * @param {{estado: string, ciclo: number, liberado?: boolean}} novo
 */
export function mudancaDaEstacao (anterior, novo) {
    if (!novo || !novo.estado) return null;
    // Evento de um ciclo que já passou (chegou fora de ordem): ignora.
    if (anterior && novo.ciclo < anterior.ciclo) return null;
    // Ciclo novo: o facilitador reiniciou o grupo.
    if (anterior && novo.ciclo > anterior.ciclo) {
        return novo.estado === 'finalizado' ? MUDANCA.FINALIZADA : MUDANCA.REINICIADA;
    }
    if (anterior?.estado === novo.estado) {
        // O grupo já tinha finalizado sozinho (todos concluíram) e o facilitador agora o libera:
        // o estado é o mesmo, mas é o comando dele que tira todo mundo da tela de resultado.
        if (novo.estado === 'finalizado' && novo.liberado === true && anterior.liberado !== true) return MUDANCA.FINALIZADA;
        return null;
    }

    if (novo.estado === 'pausado') return MUDANCA.PAUSADA;
    if (novo.estado === 'finalizado') return MUDANCA.FINALIZADA;
    if (novo.estado === 'iniciado') return anterior?.estado === 'pausado' ? MUDANCA.RETOMADA : MUDANCA.INICIADA;
    return null;
}

/** Apaga o progresso que este tablet guardou da fase, para este participante. */
export function limparProgressoDaFase (fase, participanteId, armazenamento = globalThis.localStorage) {
    const alvo = FASES_DO_GRUPO[fase];
    if (!alvo || !participanteId) return;
    try { armazenamento?.removeItem(`${alvo.chave}${participanteId}`); } catch { /* sem storage */ }
}

/**
 * Liga os avisos da fonte de dados às cenas.
 *
 * @param {object} deps
 * @param {{aoEstacao: Function, registrarConclusao: Function, reabrirFase?: Function}} deps.fonte
 * @param {{isActive: Function, pause: Function, resume: Function, stop: Function, start: Function}} deps.cenas
 * @param {() => (string|null)} deps.participanteId
 * @param {{mostrar: Function, esconder: Function}} deps.aviso faixa "atividade pausada" (`mostrar(texto?)`)
 * @param {Storage} [deps.armazenamento]
 * @param {() => number} [deps.agora] relógio em ms
 */
export function ligarEstacaoDoGrupo ({
    fonte, cenas, participanteId, aviso, armazenamento = globalThis.localStorage, agora = () => Date.now()
}) {
    const ativas = (fase) => (FASES_DO_GRUPO[fase]?.cenas ?? []).filter((cena) => cenas.isActive(cena) || cenas.isPaused?.(cena));
    const pausadas = new Set();
    let pausadoDesde = null;

    return fonte.aoEstacao(({ fase, mudanca, automatico = false, liberado = false }) => {
        const alvo = FASES_DO_GRUPO[fase];
        if (!alvo) return;

        if (mudanca === MUDANCA.PAUSADA) {
            // Só faz sentido congelar quem está dentro da fase.
            const dentro = ativas(fase);
            if (dentro.length === 0) return;
            dentro.forEach((cena) => { cenas.pause(cena); pausadas.add(cena); });
            pausadoDesde = agora();
            aviso.mostrar();
            return;
        }

        if (mudanca === MUDANCA.RETOMADA) {
            const parado = pausadoDesde === null ? 0 : agora() - pausadoDesde;
            pausadoDesde = null;
            alvo.cenas.filter((cena) => pausadas.has(cena)).forEach((cena) => {
                // Os prazos da fase são de relógio: o tempo pausado é devolvido antes de retomar.
                cenas.getScene?.(cena)?.progresso?.adiarPrazos?.(parado);
                cenas.resume(cena);
                pausadas.delete(cena);
            });
            aviso.esconder();
            return;
        }

        if (mudanca === MUDANCA.FINALIZADA && automatico) {
            // RN-EF-023: todos concluíram e o grupo finalizou sozinho. Ninguém é tirado da
            // tela de resultado: cada um segue para o Aguarde pelo próprio botão.
            aviso.esconder();
            fonte.registrarConclusao(fase);
            // AP10: no Scan Pixel o líder fecha o grupo; quem ainda está no enigma vai ao Aguarde.
            // Quem acabou de concluir no próprio tablet (o líder, no último "Conferir") fica: é ele
            // quem segue para o resultado e envia o placar do grupo.
            const jogo = alvo.cenas[0];
            const cena = cenas.getScene?.(jogo);
            const concluiu = Boolean(cena?.progresso?.terminou?.(cena?.enigmas?.length ?? Infinity));
            if (alvo.sairAoFinalizarSozinho && ativas(fase).includes(jogo) && !concluiu) {
                pausadas.delete(jogo);
                cenas.stop(jogo);
                cenas.start(SCENES.AGUARDE, { concluida: fase });
            }
            return;
        }

        if (mudanca === MUDANCA.FINALIZADA && !liberado) {
            // FINALIZAR só encerra a fase: quem está jogando fica parado onde está e quem está na pontuação
            // continua nela. Quem tira os jogadores da tela é PRÓXIMA ESTAÇÃO (`liberado`), mais abaixo.
            const emJogo = ativas(fase).filter((cena) => !(alvo.resultado ?? []).includes(cena));
            emJogo.forEach((cena) => { cenas.pause(cena); pausadas.add(cena); });
            pausadoDesde = null;
            if (emJogo.length > 0) aviso.mostrar?.(TEXTO_ENCERRADA);
            else aviso.esconder();
            return;
        }

        if (mudanca === MUDANCA.FINALIZADA || mudanca === MUDANCA.REINICIADA) {
            aviso.esconder();
            const dentro = ativas(fase);
            dentro.forEach((cena) => { pausadas.delete(cena); cenas.stop(cena); });
            pausadoDesde = null;

            if (mudanca === MUDANCA.REINICIADA) {
                // O grupo recomeça do zero: o que o tablet guardou da fase não vale mais.
                limparProgressoDaFase(fase, participanteId(), armazenamento);
                fonte.reabrirFase(fase);
                // Fora do Aguarde e das cenas da fase (Mural, Materiais...), a entrada
                // acontece quando o participante voltar ao Aguarde.
                if (dentro.length > 0) cenas.start(alvo.cenas[0]);
                return;
            }

            // Finalizada pelo facilitador: o que não foi enviado fica sem pontuação.
            fonte.registrarConclusao(fase);
            if (dentro.length > 0) cenas.start(SCENES.AGUARDE, { concluida: fase });
        }
    });
}
